import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, InputNumber, message, Modal, Spin } from 'antd';
import { CheckOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import MD5 from 'crypto-js/md5';

import request from '@/request';

/** 提取纯数字用时文本，去掉可能存在的秒单位 */
const getPlainSecondText = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return String(value).replace(/秒$/, '').trim();
};

/** 格式化用时展示文案 */
const formatSecondDisplay = (value) => {
  const text = getPlainSecondText(value);
  return text ? `${text}秒` : '-';
};

/** 校验用时输入，要求非负数字且最多两位小数 */
const validateUseTime = (value) => {
  const text = String(value ?? '').trim();
  if (!text) {
    return { valid: false, message: '请输入用时', value: 0, text: '' };
  }
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return {
      valid: false,
      message: '用时格式错误，请输入秒.毫秒格式，如 1.28',
      value: 0,
      text,
    };
  }
  const numberValue = Number(text);
  if (!Number.isFinite(numberValue) || numberValue < 0) {
    return { valid: false, message: '用时不能为负数', value: 0, text };
  }
  return { valid: true, message: '', value: numberValue, text };
};

/** 把原始用时转换为可编辑输入值 */
const getInitialUseTime = (value) => {
  const text = getPlainSecondText(value);
  return validateUseTime(text).valid ? text : '';
};

/** 生成计分项默认值，选项型默认选最后一个配置项 */
const buildDefaultScores = (groups = []) => {
  const defaultScores = {};
  groups.forEach((group) => {
    (group.items || []).forEach((item) => {
      if (
        (item.scoreTypeId === 1 || item.scoreTypeId === 8) &&
        item.options?.length
      ) {
        defaultScores[item.id] = item.options[item.options.length - 1].id;
      }
    });
  });
  return defaultScores;
};

/** 读取可选数值，空值时返回 undefined */
const getOptionalNumber = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

/** 按小程序计分逻辑计算总成绩 */
const calcTotalScore = ({ baseScore, costTime, itemGroups, scores }) => {
  let totalScore = Number(baseScore || 0);
  const items = itemGroups.flatMap((group) => group.items || []);
  let scoreInvalid = false;

  items.forEach((item) => {
    const value = scores[item.id];
    if (!value) return;

    if (item.scoreTypeId === 1) {
      const option = item.options?.find((o) => o.id === value);
      if (option) totalScore += Number(option.score || 0);
    } else if (item.scoreTypeId === 2) {
      totalScore += Number(value || 0);
    } else if (item.scoreTypeId === 3) {
      totalScore += Number(value || 0) * Number(item.attr?.per || 0);
    } else if (item.scoreTypeId === 5) {
      totalScore -= Number(value || 0);
    } else if (item.scoreTypeId === 7) {
      scoreInvalid = true;
    } else if (item.scoreTypeId === 8) {
      const option = item.options?.find((o) => o.id === value);
      if (option) totalScore -= Number(option.score || 0);
    } else if (item.scoreTypeId === 9) {
      if (value) totalScore += Number(costTime || 0);
    } else if (item.scoreTypeId === 4) {
      const count = Number(value || 0);
      const tolerate = Number(item.attr?.tolerate ?? 0);
      if (tolerate === 0 || count > tolerate) {
        totalScore -= Number(count - tolerate) * Number(item.attr?.per || 0);
      }
      const maxTolerate = getOptionalNumber(item.attr?.maxTolerate);
      if (maxTolerate !== undefined && count >= maxTolerate) {
        totalScore = 0;
      }
    }
  });

  const finalScore = scoreInvalid ? 0 : Number(totalScore.toFixed(2));
  // 新成绩展示和提交时最小只能为 0。
  return Math.max(0, finalScore);
};

/** EditScoreModal：按赛项计分规则修改选手成绩 */
export default function EditScoreModal({
  open,
  activeItem,
  player,
  onCancel,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState({ baseScore: 0 });
  const [itemGroups, setItemGroups] = useState([]);
  const [scores, setScores] = useState({});
  const [useTime, setUseTime] = useState('');
  const [password, setPassword] = useState('');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const timeValidation = useMemo(() => validateUseTime(useTime), [useTime]);

  const totalScore = useMemo(() => {
    return calcTotalScore({
      baseScore: rules.baseScore,
      costTime: timeValidation.valid ? timeValidation.value : 0,
      itemGroups,
      scores,
    });
  }, [itemGroups, rules.baseScore, scores, timeValidation]);

  /** 按计分类型把计分项拆成得分项和扣分项 */
  const scoreColumns = useMemo(() => {
    const columns = [
      { key: 'gain', title: '得分项', scoreTypeIds: [1, 2, 3, 9], groups: [] },
      { key: 'deduct', title: '扣分项', scoreTypeIds: [4, 5, 7, 8], groups: [] },
    ];

    itemGroups.forEach((group) => {
      columns.forEach((column) => {
        const items = (group.items || []).filter((item) =>
          column.scoreTypeIds.includes(item.scoreTypeId)
        );
        if (items.length) {
          column.groups.push({ ...group, items });
        }
      });
    });

    return columns;
  }, [itemGroups]);

  useEffect(() => {
    if (!open) return;

    let ignore = false;

    /** 拉取当前赛项计分配置并初始化表单 */
    const loadScoreItems = async () => {
      setPassword('');
      setPasswordModalOpen(false);
      setUseTime(getInitialUseTime(player?.useTime));
      setRules({ baseScore: 0 });
      setItemGroups([]);
      setScores({});

      if (!activeItem?.id) return;

      setLoading(true);
      try {
        const result = await request.get(
          `/events/admin/score/item/list/${activeItem.id}`
        );
        if (ignore) return;
        const groups = result?.groups || [];
        setRules(result?.rule || { baseScore: 0 });
        setItemGroups(groups);
        setScores(buildDefaultScores(groups));
      } catch (error) {
        if (!ignore) message.error(error.message || '计分项加载失败');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadScoreItems();

    return () => {
      ignore = true;
    };
  }, [activeItem?.id, open, player?.useTime]);

  /** 更新单个计分项值 */
  const updateItemScore = useCallback((itemId, value) => {
    setScores((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  /** 处理次数型计分项加减，并限制在规则最大次数内 */
  const updateCountScore = useCallback((item, step) => {
    setScores((prev) => {
      const current = Number(prev[item.id] || 0);
      const max = getOptionalNumber(item.attr?.max);
      const nextValue = Math.max(
        0,
        Math.min(current + step, max ?? Infinity)
      );
      return { ...prev, [item.id]: nextValue };
    });
  }, []);

  /** 关闭成绩修改弹窗并清理密码确认状态 */
  const closeScoreModal = useCallback(() => {
    setPassword('');
    setPasswordModalOpen(false);
    onCancel?.();
  }, [onCancel]);

  /** 打开密码确认弹窗前先校验新用时 */
  const openPasswordModal = useCallback(() => {
    const currentTime = validateUseTime(useTime);
    if (!currentTime.valid) {
      message.warning(currentTime.message);
      return;
    }
    setPassword('');
    setPasswordModalOpen(true);
  }, [useTime]);

  /** 关闭密码确认弹窗并清空密码 */
  const closePasswordModal = useCallback(() => {
    setPassword('');
    setPasswordModalOpen(false);
  }, []);

  /** 提交修改成绩信息到后端接口 */
  const submitScoreChange = useCallback(async () => {
    const currentTime = validateUseTime(useTime);
    if (!currentTime.valid) {
      message.warning(currentTime.message);
      return;
    }
    if (!password.trim()) {
      message.warning('请输入密码');
      return;
    }

    try {
      await request.post('/events/admin/dynamic/score/update', {
        itemId: String(activeItem?.id ?? ''),
        userId: String(player?.userId ?? ''),
        score: totalScore,
        useTime: currentTime.value,
        password: MD5(password.trim()).toString(),
        reason: '赛后修正',
      });
      message.success('成绩修改成功');
      setPassword('');
      setPasswordModalOpen(false);
      if (onSuccess) {
        onSuccess();
      } else {
        closeScoreModal();
      }
    } catch (error) {
      message.error(error.message || '成绩修改失败');
    }
  }, [
    activeItem?.id,
    closeScoreModal,
    onSuccess,
    password,
    player?.playerId,
    totalScore,
    useTime,
  ]);

  /** 渲染不同类型的计分控件 */
  const renderScoreControl = (item) => {
    if (item.scoreTypeId === 1 || item.scoreTypeId === 8) {
      return (
        <div className="flex max-w-full flex-wrap justify-end gap-2">
          {(item.options || []).map((option) => (
            <Button
              key={option.id}
              type={scores[item.id] === option.id ? 'primary' : 'default'}
              onClick={() => updateItemScore(item.id, option.id)}
              className="min-w-15"
            >
              {option.score}
            </Button>
          ))}
        </div>
      );
    }

    if (item.scoreTypeId === 2 || item.scoreTypeId === 5) {
      return (
        <InputNumber
          min={getOptionalNumber(item.attr?.min) ?? 0}
          max={getOptionalNumber(item.attr?.max)}
          value={scores[item.id] ?? 0}
          onChange={(value) => updateItemScore(item.id, value ?? 0)}
          className="w-30 max-w-full"
        />
      );
    }

    if (item.scoreTypeId === 3 || item.scoreTypeId === 4) {
      return (
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
          {item.attr?.per && (
            <span className="text-sm text-slate-600 whitespace-nowrap">
              {item.scoreTypeId === 3 ? '+' : '-'} {item.attr.per} 分
            </span>
          )}
          <Button
            shape="circle"
            icon={<MinusOutlined />}
            onClick={() => updateCountScore(item, -1)}
          />
          <span className="w-5 text-center text-slate-700">
            {scores[item.id] || 0}
          </span>
          <Button
            shape="circle"
            type="primary"
            ghost
            icon={<PlusOutlined />}
            onClick={() => updateCountScore(item, 1)}
          />
          <span className="text-slate-700">次</span>
        </div>
      );
    }

    if (item.scoreTypeId === 7 || item.scoreTypeId === 9) {
      return (
        <Button
          shape="circle"
          type={scores[item.id] ? 'primary' : 'default'}
          icon={scores[item.id] ? <CheckOutlined /> : null}
          onClick={() => updateItemScore(item.id, !scores[item.id])}
        />
      );
    }

    return <span className="text-sm text-slate-400">暂不支持该计分类型</span>;
  };

  /** 渲染单个计分项行 */
  const renderScoreItem = (item) => (
    <div
      key={item.id}
      className="min-h-16 border-b border-slate-100 py-3 last:border-b-0"
    >
      <div className="mb-2 flex min-w-0 items-center gap-3">
        <span className="h-5 w-1 shrink-0 rounded-full bg-orange-300" />
        <span className="min-w-0 flex-1 break-words text-slate-700">
          {item.name}
        </span>
      </div>
      <div className="min-w-0">{renderScoreControl(item)}</div>
    </div>
  );

  /** 渲染得分项或扣分项分栏 */
  const renderScoreColumn = (column) => (
    <div key={column.key} className="min-w-0">
      <h3 className="sticky top-0 z-10 mb-3 bg-white py-2 text-lg font-semibold text-slate-800">
        {column.title}
      </h3>
      <div className="space-y-4">
        {column.groups.length === 0 && !loading && (
          <div className="rounded-lg border border-slate-100 bg-white py-10 text-center text-slate-400">
            暂无{column.title}
          </div>
        )}
        {column.groups.map((group) => (
          <div
            key={`${column.key}-${group.name}`}
            className="rounded-lg border border-slate-100 bg-white p-4"
          >
            <div>{(group.items || []).map(renderScoreItem)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Modal
        title="修改成绩"
        width="min(960px, 92vw)"
        open={open}
        onCancel={closeScoreModal}
        footer={null}
        maskClosable={false}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 120px)',
            overflow: 'hidden',
          },
        }}
      >
        <div className="flex max-h-[calc(100vh-120px)] flex-col gap-4 overflow-hidden">
          <div className="grid shrink-0 grid-cols-2 gap-3 rounded-lg bg-slate-50 p-4 text-sm">
            <div className="min-w-0">
              <div className="text-slate-500">旧成绩</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {player?.part1 ?? '-'}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-500">新成绩</div>
              <div className="mt-1 text-lg font-semibold text-blue-600">
                {totalScore}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-500">旧用时</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {formatSecondDisplay(player?.useTime)}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-500">新用时</div>
              <Input
                value={useTime}
                onChange={(e) => setUseTime(e.target.value)}
                placeholder="请输入用时，如 1.28"
                suffix="秒"
                status={useTime && !timeValidation.valid ? 'error' : ''}
                className="mt-1"
              />
              {useTime && !timeValidation.valid && (
                <div className="mt-1 break-words text-sm text-red-500">
                  {timeValidation.message}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pr-1">
            <Spin spinning={loading}>
              {itemGroups.length === 0 && !loading && (
                <div className="py-12 text-center text-slate-400">
                  暂无计分项配置
                </div>
              )}
              {itemGroups.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  {scoreColumns.map(renderScoreColumn)}
                </div>
              )}
            </Spin>
          </div>

          <div className="shrink-0 border-t border-slate-200 bg-white pt-4">
            <div className="flex justify-end gap-2">
              <Button onClick={closeScoreModal}>取消</Button>
              <Button type="primary" onClick={openPasswordModal}>
                提交修改
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="密码确认"
        width={360}
        open={open && passwordModalOpen}
        onCancel={closePasswordModal}
        maskClosable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={closePasswordModal}>取消</Button>
            <Button type="primary" onClick={submitScoreChange}>
              确认提交
            </Button>
          </div>
        }
      >
        <Input.Password
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={submitScoreChange}
          placeholder="请输入登录密码"
        />
      </Modal>
    </>
  );
}
